import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useApi } from './ApiContext';
import { useIdleTimeout } from '../hooks/useIdleTimeout';

export type UserRole = 'ADMINISTRADOR' | 'USUARIO' | 'SUPERVISOR' | 'OPERADOR' | 'LECTOR' | 'GESTOR';

export type PermissionAction = 'view' | 'edit' | 'delete' | 'export' | 'import' | 'adjust_stock' | 'approve' | 'view_reports' | 'manage_settings';

export interface User {
  id: number;
  username: string;
  role: UserRole;
  is_active: boolean;
  google_id?: string;
  google_email?: string;
  permissions?: Array<{
    id: number;
    section: string;
    can_view: boolean;
    can_edit: boolean;
    can_delete?: boolean;
    can_export?: boolean;
    can_import?: boolean;
    can_adjust_stock?: boolean;
    can_approve?: boolean;
    can_view_reports?: boolean;
    can_manage_settings?: boolean;
  }>;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isLoading: boolean; // Alias para compatibilidad
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasPermission: (section: string, action: PermissionAction) => boolean;
  getGoogleAuthUrl: () => Promise<string>;
  loginWithGoogle: (authData: any) => Promise<void>;
  isAdmin: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'seekers_stock_user';

// Solo desactivar autenticación en desarrollo local si se configura explícitamente
const DISABLE_AUTH = import.meta.env.DEV && import.meta.env.VITE_DISABLE_AUTH === 'true';

export function AuthProvider({ children }: { children: ReactNode }) {
  const { api } = useApi();

  // Cargar usuario desde localStorage de forma síncrona al inicio
  const getInitialUser = (): User | null => {
    if (DISABLE_AUTH) {
      return {
        id: 1,
        username: 'debug_user',
        role: 'ADMINISTRADOR',
        is_active: true,
        permissions: [],
      };
    }

    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        const { token, ...userData } = parsedData;
        if (userData.id && userData.username && token) {
          return userData as User;
        }
      }
    } catch (error) {
      console.error('Error al cargar usuario inicial desde localStorage:', error);
    }
    return null;
  };

  const [user, setUser] = useState<User | null>(getInitialUser);
  const [loading, setLoading] = useState(true);

  // Configurar token desde localStorage al inicio si existe
  useEffect(() => {
    if (DISABLE_AUTH) {
      console.warn('⚠️  BYPASS DE AUTENTICACIÓN ACTIVO EN FRONTEND - SOLO PARA DEBUGGING LOCAL');
      setLoading(false);
      return;
    }

    // Si hay usuario cargado, configurar token desde localStorage inmediatamente
    if (user) {
      try {
        const storedData = localStorage.getItem(STORAGE_KEY);
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          if (parsedData.token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${parsedData.token}`;
          }
        }
      } catch (error) {
        console.error('Error al configurar token inicial:', error);
      }
      // Si ya tenemos usuario, podemos marcar como no cargando temporalmente
      // hasta que se verifique con el backend
      setLoading(false);
    } else {
      // No hay usuario, no hacer nada más
      setLoading(false);
    }
  }, [api, user]);

  const checkAuth = useCallback(async () => {
    // BYPASS TEMPORAL
    if (DISABLE_AUTH) {
      const mockUser: User = {
        id: 1,
        username: 'debug_user',
        role: 'ADMINISTRADOR',
        is_active: true,
        permissions: [],
      };
      setUser(mockUser);
      setLoading(false);
      return;
    }

    // Si user ya está cargado desde localStorage, verificar token
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) {
      setLoading(false);
      return;
    }

    try {
      const parsedData = JSON.parse(storedData);
      const { token, ...userData } = parsedData;

      if (!token) {
        throw new Error('No token found');
      }

      // Si ya tenemos datos del usuario en localStorage, establecerlos temporalmente
      // para evitar mostrar el login mientras verificamos
      if (userData.id && userData.username) {
        setUser(userData as User);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      // Verificar token con el backend
      const response = await api.get('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const verifiedUserData = response.data;

      // Actualizar permisos
      try {
        const permissionsResponse = await api.get(`/auth/users/${verifiedUserData.id}/permissions`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        verifiedUserData.permissions = permissionsResponse.data;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('No se pudieron obtener los permisos del usuario');
        }
        verifiedUserData.permissions = userData.permissions || [];
      }

      // Actualizar con datos verificados del servidor
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...verifiedUserData, token }));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(verifiedUserData);
    } catch (error: any) {
      console.error('Error al verificar autenticación:', error);

      // Solo limpiar si es un error de autenticación (401/403), no errores de servidor o red
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      const isNetworkError = !error.response && error.request;
      const isServerError = error.response?.status >= 500;

      if (isAuthError) {
        // Token inválido o expirado - limpiar
        localStorage.removeItem(STORAGE_KEY);
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      } else if (isNetworkError || isServerError) {
        // Error de red o del servidor - mantener datos del localStorage y usuario temporal
        console.warn('Error de red/servidor al verificar autenticación, manteniendo sesión local');
        const storedData = localStorage.getItem(STORAGE_KEY);
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData);
            const { token, ...userData } = parsedData;
            if (userData.id && userData.username && token) {
              setUser(userData as User);
              api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
          } catch (e) {
            console.error('Error al restaurar usuario desde localStorage:', e);
          }
        }
      } else {
        // Otro error - mantener sesión por si acaso, pero loguear
        console.warn('Error desconocido al verificar autenticación, manteniendo sesión local');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Verificar autenticación con backend después de cargar desde localStorage
  // Solo ejecutar una vez al montar el componente
  useEffect(() => {
    let isMounted = true;

    const verifyAuth = async () => {
      if (DISABLE_AUTH) {
        return;
      }

      // Si ya tenemos usuario cargado desde localStorage, verificar con backend en segundo plano
      // Solo si aún no se ha verificado (evitar bucles)
      if (user && isMounted) {
        await checkAuth();
      }
    };

    verifyAuth();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  // Escuchar eventos de actualización de permisos
  useEffect(() => {
    const handlePermissionsUpdated = () => {
      if (user && !DISABLE_AUTH) {
        // Recargar permisos del usuario actual
        checkAuth();
      }
    };

    window.addEventListener('permissions-updated', handlePermissionsUpdated);
    return () => {
      window.removeEventListener('permissions-updated', handlePermissionsUpdated);
    };
  }, [user, checkAuth]);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { user: userData, token } = response.data;

      // Obtener permisos del usuario
      try {
        const permissionsResponse = await api.get(`/auth/users/${userData.id}/permissions`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        userData.permissions = permissionsResponse.data;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('No se pudieron obtener los permisos del usuario después del login');
        }
        userData.permissions = [];
      }

      // Guardar usuario y token en localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...userData, token }));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
    } catch (error: any) {
      const message = error.response?.data?.error || 'Error al iniciar sesión';
      throw new Error(message);
    }
  };

  const getGoogleAuthUrl = async (): Promise<string> => {
    const response = await api.get('/auth/google-url');
    return response.data.authUrl;
  };

  const loginWithGoogle = async (authData: any) => {
    const { user: userData, token } = authData;

    // Obtener permisos del usuario
    try {
      const permissionsResponse = await api.get(`/auth/users/${userData.id}/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      userData.permissions = permissionsResponse.data;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('No se pudieron obtener los permisos del usuario después del login de Google');
      }
      userData.permissions = [];
    }

    // Guardar usuario y token en localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...userData, token }));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error al hacer logout:', error);
    } finally {
      // Limpiar estado
      localStorage.removeItem(STORAGE_KEY);
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
    }
  }, [api]);

  // Logout automático por inactividad (16 horas)
  useIdleTimeout({
    timeout: 16 * 60 * 60 * 1000, // 16 horas
    enabled: !DISABLE_AUTH && !!user,
    onIdle: async () => {
      if (import.meta.env.DEV) {
        console.log('⚠️ Inactividad detectada, cerrando sesión automáticamente...');
      }
      await logout();
    },
  });

  const hasPermission = (section: string, action: PermissionAction): boolean => {
    // SISTEMA DE PERMISOS DESHABILITADO - Todos los usuarios autenticados tienen acceso completo
    if (!user) return false;
    return true;
  };

  // SISTEMA DE PERMISOS DESHABILITADO - Todos los usuarios autenticados tienen permisos de administrador
  const isAdmin = DISABLE_AUTH ? true : (user !== null);
  const isAuthenticated = DISABLE_AUTH ? true : (user !== null);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isLoading: loading,
        login,
        logout,
        checkAuth,
        hasPermission,
        getGoogleAuthUrl,
        loginWithGoogle,
        isAdmin,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
